import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@deere/fuel-react/PageHeader.js';
import { PageHeaderTitle } from '@deere/fuel-react/PageHeaderTitle.js';
import { PageHeaderActions } from '@deere/fuel-react/PageHeaderActions.js';
import { PageContent } from '@deere/fuel-react/PageContent.js';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import SupplierDropDownSearch from './SupplierDropDownSearch';
import SupplierMultiSelect from './SupplierMultiSelect';
import NoAccessEmployee from './NoAccessEmployee';
import MessageBanner from '../shared/MessageBanner';
import { useUserInfo } from '../shared/UserInfo';
import { useAPIProgress } from '../shared/APIProgress';
import { useStatusMessages } from '../shared/StatusMessages';
import {
  DataGridPro,
  GridColDef,
  gridExpandedSortedRowIdsSelector,
  GridSortModel,
  Toolbar,
  useGridApiContext
} from '@mui/x-data-grid-pro';
import { supplierSpendShiftGridStyle } from '../shared/spendUtils';
import {
  getCiProjectSpendShift,
  getSupplierSpendShift,
  getSuppliersSroSbmMap,
  searchSuppliersByQuery
} from '../shared/APIHelper';
import { supplierGroupApi } from '../utils/api-clients/api-clients';
import { getFormattedUserNameWithId } from '../shared/UserLogin';
import { fetchJsonWithAuth } from '../shared/fetch-json.util';
import { transformMasterScopeResponse } from '../shared/scopeUtils';
import SupplierSpendShiftGraph from './SupplierSpendShiftGraph';
import SupplierSpendShiftSummary from './SupplierSpendShiftSummary';
import ScopeQuickFilter from './ScopeQuickFilter';
import Grid from '@mui/material/Grid';

import {
  CI_PROJECTS_LABEL,
  CI_PROJECTS_TOOLTIP,
  SELECTION_MODE,
  SRO_SBM_API_ERR_MSG
} from '../shared/AppConstants';
import type {
  CiProjectSpendShiftApiResponse,
  JdsnGroupItem,
  SelectionMode,
  SpendEntry,
  SpendShiftRow,
  SupplierSpendShiftRequestDTO,
  SupplierSpendShiftResponse,
  SupplierSpendBreakdown
} from './SupplierSpendShift.types';
import {
  mapSupplierResult,
  MAX_SUPPLIERS,
  normalizeSupplierNumber,
  parseSuppliersFromUrl,
  serializeSuppliersToUrl,
  type SupplierOption
} from './supplierSelectionUtils';
import { exportSupplierSpendShiftPartDetails } from './supplierSpendShiftPartDetailDownload';
import { getEnvVar } from '../utils/getEnvVar';

import { operations as SupplierGroupOperations } from '../types/apis/supplierGroup/schema-typescript';

export function formatCurrency(amount: number | null | undefined): string {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

export function formatSpendAndPercentage(
  spendEntry: SpendEntry | null | undefined,
  showPercentage: boolean = true
): string {
  if (!spendEntry) return '—';
  const amount = formatCurrency(spendEntry.amount);

  if (showPercentage && spendEntry.percentage !== null) {
    return `${amount} (${spendEntry.percentage}%)`;
  }

  return amount;
}

export function formatPieces(amount: number | null | undefined): string {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

export function formatPiecesAndPercentage(
  spendEntry: SpendEntry | null | undefined,
  showPercentage: boolean = true
): string {
  if (!spendEntry) return '—';
  const amount = formatPieces(spendEntry.amount);
  if (showPercentage && spendEntry.percentage !== null) {
    return `${amount} (${spendEntry.percentage}%)`;
  }
  return amount;
}

function getSpendShiftGridRow(
  valueOrParams: unknown,
  row?: SpendShiftRow
): SpendShiftRow | undefined {
  if (row) return row;
  if (
    valueOrParams &&
    typeof valueOrParams === 'object' &&
    'row' in valueOrParams
  ) {
    return (valueOrParams as { row?: SpendShiftRow }).row;
  }
  return undefined;
}

function enrichWithCiData(
  response: SupplierSpendShiftResponse,
  ciApiResponse: CiProjectSpendShiftApiResponse,
  displayMode: 'spend' | 'pieces'
): SupplierSpendShiftResponse {
  const rawCiEntries = ciApiResponse.projects;

  const fiscalYears = response.fiscalYears; // e.g. ["2027", "2028", "2029"]

  // Group flat entries by projectId using a Map to preserve API insertion order.
  // A plain object {} sorts numeric string keys ascending, losing the API's implDate ordering.
  const grouped = new Map<string, typeof rawCiEntries>();
  for (const entry of rawCiEntries) {
    if (!grouped.has(entry.projectId)) grouped.set(entry.projectId, []);
    grouped.get(entry.projectId)!.push(entry);
  }

  // 3 + 4. Build CiProject[], apply cumulative carry-forward, collapse unitCode to "Multiple" if >1
  const ciProjects = [...grouped.entries()].map(([projectId, entries]) => {
    const firstEntry = entries[0];

    // 4. Collapse unit codes
    const uniqueUnitCodes = [...new Set(entries.map((e) => e.unitCode))];
    const unitCode =
      uniqueUnitCodes.length > 1 ? 'Multiple' : uniqueUnitCodes[0];

    // Map fiscalYear string -> spendChange
    const fyMap: Record<string, number> = {};
    for (const e of entries) {
      const value =
        displayMode === 'pieces' ? (e.piecesChange ?? 0) : (e.spendChange ?? 0);
      fyMap[String(e.fiscalYear)] = (fyMap[String(e.fiscalYear)] ?? 0) + value;
    }

    // Sum spend from years not shown as FY columns (current year) into R12 Forecasted
    const columnYears = new Set(fiscalYears.map(String));
    const r12ForecastAmount = Object.entries(fyMap)
      .filter(([fy]) => !columnYears.has(fy))
      .reduce((sum, [, val]) => sum + val, 0);

    // 3. Build SpendEntry[] aligned to response.fiscalYears — per-year delta only (no carry-forward, same as PDP)
    const fiscalYearEntries: SpendEntry[] = fiscalYears.map((fy) => ({
      amount: fyMap[String(fy)] ?? 0,
      percentage: null,
      label: `FY${fy}`
    }));

    return {
      projectId,
      projectTitle: firstEntry.projectTitle,
      projectStatus: firstEntry.projectStatus ?? null,
      unitCode,
      implDate: firstEntry.implDate,
      r12InvoiceSpend: { amount: 0, percentage: null, label: '' },
      r12ForecastSpend: {
        amount: r12ForecastAmount,
        percentage: null,
        label: ''
      },
      fiscalYears: fiscalYearEntries
    };
  });

  // 5. Compute totalCISpends by summing all CI projects per FY
  const totalCIFiscalYears: SpendEntry[] = fiscalYears.map((fy, idx) => ({
    amount: ciProjects.reduce(
      (sum, p) => sum + (p.fiscalYears[idx]?.amount ?? 0),
      0
    ),
    percentage: null,
    label: `FY${fy}`
  }));

  const totalCIR12ForecastAmount = ciProjects.reduce(
    (sum, p) => sum + p.r12ForecastSpend.amount,
    0
  );

  const totalCISpends = {
    r12InvoiceSpend: { amount: 0, percentage: null, label: '' },
    r12ForecastSpend: {
      amount: totalCIR12ForecastAmount,
      percentage: null,
      label: ''
    },
    fiscalYears: totalCIFiscalYears
  };

  // 6. Recompute totalSpends.fiscalYears = existing PDP+current total + cumulative CI impact per FY
  // totalCIFiscalYears are per-year deltas (for display rows), but the total spend baseline must
  // absorb CI changes cumulatively so subsequent years reflect the lower baseline.
  // Seed with totalCIR12ForecastAmount so FY2026 (pre-column) CI changes carry forward into all FY columns.
  let cumulativeCITotal = totalCIR12ForecastAmount;
  const updatedTotalSpends = {
    ...response.totalSpends,
    r12ForecastSpend: {
      ...response.totalSpends.r12ForecastSpend,
      amount:
        response.totalSpends.r12ForecastSpend.amount + totalCIR12ForecastAmount
    },
    fiscalYears: response.totalSpends.fiscalYears.map((fyEntry, idx) => {
      cumulativeCITotal += totalCIFiscalYears[idx]?.amount ?? 0;
      return { ...fyEntry, amount: fyEntry.amount + cumulativeCITotal };
    })
  };

  const r12InvTotal = updatedTotalSpends.r12InvoiceSpend.amount;
  const r12FcstTotal = updatedTotalSpends.r12ForecastSpend.amount;

  // 7a. Enrich CI project R12 and fiscal year entries with percentages relative to total spend
  const ciProjectsWithPercentages = ciProjects.map((project) => ({
    ...project,
    r12InvoiceSpend: {
      ...project.r12InvoiceSpend,
      percentage:
        r12InvTotal !== 0
          ? Math.round((project.r12InvoiceSpend.amount / r12InvTotal) * 100)
          : null
    },
    r12ForecastSpend: {
      ...project.r12ForecastSpend,
      percentage:
        r12FcstTotal !== 0
          ? Math.round((project.r12ForecastSpend.amount / r12FcstTotal) * 100)
          : null
    },
    fiscalYears: project.fiscalYears.map((fyEntry, idx) => {
      const totalAmount = updatedTotalSpends.fiscalYears[idx]?.amount ?? 0;
      const percentage =
        totalAmount !== 0
          ? Math.round((fyEntry.amount / totalAmount) * 100)
          : null;
      return { ...fyEntry, percentage };
    })
  }));

  // 7b. Enrich totalCISpends R12 and fiscal year entries with percentages relative to total spend
  const totalCISpendsWithPercentages = {
    ...totalCISpends,
    r12InvoiceSpend: {
      ...totalCISpends.r12InvoiceSpend,
      percentage:
        r12InvTotal !== 0
          ? Math.round(
              (totalCISpends.r12InvoiceSpend.amount / r12InvTotal) * 100
            )
          : null
    },
    r12ForecastSpend: {
      ...totalCISpends.r12ForecastSpend,
      percentage:
        r12FcstTotal !== 0
          ? Math.round(
              (totalCISpends.r12ForecastSpend.amount / r12FcstTotal) * 100
            )
          : null
    },
    fiscalYears: totalCISpends.fiscalYears.map((fyEntry, idx) => {
      const totalAmount = updatedTotalSpends.fiscalYears[idx]?.amount ?? 0;
      const percentage =
        totalAmount !== 0
          ? Math.round((fyEntry.amount / totalAmount) * 100)
          : null;
      return { ...fyEntry, percentage };
    })
  };

  // 7c. Update currentSpends.fiscalYears so year N+1 baseline reflects cumulative CI changes through year N
  // (totalSpend[N] becomes currentSpend[N+1], same relationship the backend uses for PDP)
  // Seed with totalCIR12ForecastAmount so FY2026 CI impact is included from FY2027 onward.
  let prevCumulativeCI = totalCIR12ForecastAmount;
  const updatedCurrentSpends = {
    ...response.currentSpends,
    fiscalYears: response.currentSpends.fiscalYears.map((fyEntry, idx) => {
      const adjustment = prevCumulativeCI;
      prevCumulativeCI += totalCIFiscalYears[idx]?.amount ?? 0;
      const newAmount = fyEntry.amount + adjustment;
      const totalAmount = updatedTotalSpends.fiscalYears[idx]?.amount ?? 0;
      const percentage =
        totalAmount !== 0 ? Math.round((newAmount / totalAmount) * 100) : null;
      return { ...fyEntry, amount: newAmount, percentage };
    })
  };

  // 7. Return enriched response
  return {
    ...response,
    currentSpends: updatedCurrentSpends,
    ciProjects: ciProjectsWithPercentages,
    totalCISpends: totalCISpendsWithPercentages,
    totalSpends: updatedTotalSpends,
    errorStatus: {
      invoiceSpendError: response.errorStatus?.invoiceSpendError ?? false,
      forecastSpendError: response.errorStatus?.forecastSpendError ?? false,
      pdpProgramsError: response.errorStatus?.pdpProgramsError ?? false,
      ciProjectsError: ciApiResponse.hadError === true
    }
  };
}

function CustomToolbar({
  supplierName,
  spendShiftDisplayMode
}: {
  supplierName: string;
  spendShiftDisplayMode: 'spend' | 'pieces';
}) {
  const apiRef = useGridApiContext();
  const modeLabel =
    spendShiftDisplayMode === 'pieces' ? 'Pieces Shift' : 'Spend Shift';
  return (
    <Toolbar style={{ justifyContent: 'flex-end' }}>
      <Button
        variant={'tertiary'}
        startIcon={<fuel-icon name={'download'} />}
        onClick={() =>
          apiRef.current.exportDataAsCsv({
            fileName: `${supplierName} ${modeLabel}`,
            getRowsToExport: () => [
              'currentSpend',
              'pdpHeader',
              ...gridExpandedSortedRowIdsSelector(apiRef),
              'totalSpend'
            ]
          })
        }
      >
        {'Export'}
      </Button>
    </Toolbar>
  );
}

const sortByLabelAlphabetically = (
  a: { label: string },
  b: { label: string }
) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

type SupplierDetailResult =
  | { supplierNumber: string; failed: true }
  | {
      supplierNumber: string;
      failed: false;
      spendData: SupplierSpendShiftResponse;
    };

/** 0 = PDP program row, 1 = CI header/section row, 2 = CI project row. Keeps the two groups visually separated during sorting. */
function getGroupOrder(row: SpendShiftRow | null | undefined): number {
  if (row?.type === 'ciHeader') return 1;
  if (row?.type === 'ciProject') return 2;
  return 0;
}

export default function SupplierSpendShift() {
  const info = useUserInfo();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Capture the initial URL param once so the loading effect doesn't re-run when we write to the URL
  const initialSupplierParam = useRef(searchParams.get('supplierNumber'));
  const initialGroupParam = useRef(searchParams.get('supplierGroup'));

  const hasAccess = info?.hasAccess;
  const loggedInUser = info?.userId;

  const [selectedSuppliers, setSelectedSuppliers] = useState<SupplierOption[]>(
    []
  );
  const [selectedSupplierGroup, setSelectedSupplierGroup] = useState<
    any | null
  >(null);
  const [jdsnGroupOptions, setJdsnGroupOptions] = useState<any[]>([]);
  const [jdsnGroupsLoading, setJdsnGroupsLoading] = useState<boolean>(false);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(
    initialGroupParam.current
      ? SELECTION_MODE.SUPPLIER_GROUP
      : SELECTION_MODE.SINGLE_SUPPLIER
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groupMembers, setGroupMembers] = useState<
    SupplierGroupOperations['handleSearchRequest']['responses']['200']['content']['application/json']['results']
  >([]);
  const [showGroupMembersModal, setShowGroupMembersModal] =
    useState<boolean>(false);

  const {
    statusMessages,
    setStatusErrorMsg,
    clearAllMessages,
    setStatusWarningMsg
  } = useStatusMessages();
  const { apiInProgressCount } = useAPIProgress();

  const [supplierSpendData, setSupplierSpendData] = useState<
    SupplierSpendShiftResponse | undefined
  >(undefined);
  const [supplierBreakdown, setSupplierBreakdown] = useState<
    SupplierSpendBreakdown[]
  >([]);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [sroSbmInfo, setSroSbmInfo] = useState<any | null | undefined>(null);

  // Scope filter state
  const [masterScope, setMasterScope] = useState<any[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<string[] | null>(null);

  // Spend/Pieces display mode for the Spend Shift page
  const [spendShiftDisplayMode, setSpendShiftDisplayMode] = useState<
    'spend' | 'pieces'
  >('spend');

  const graphSuppliers = useMemo<SupplierOption[]>(() => {
    if (selectionMode === SELECTION_MODE.SINGLE_SUPPLIER) {
      return selectedSuppliers;
    }

    return groupMembers.map((member) =>
      mapSupplierResult({
        supplierNumber: member.supplierNumber,
        supplierName: member.supplier?.supplierName ?? member.supplierNumber
      })
    );
  }, [groupMembers, selectedSuppliers, selectionMode]);

  // Check if supplier was passed from another page via location state
  useEffect(() => {
    if (location.state?.supplier) {
      const supplier = mapSupplierResult({
        supplierNumber: location.state.supplier.value,
        supplierName: String(location.state.supplier.label ?? '')
          .split(' - ')
          .slice(1)
          .join(' - ')
      });
      setSelectedSuppliers([supplier]);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('supplierNumber', String(supplier.value));
          next.delete('supplierGroup');
          return next;
        },
        { replace: true }
      );
    }
    if (location.state?.supplierGroup) {
      setSelectedSupplierGroup(location.state.supplierGroup);
    }
  }, [location.state, setSearchParams]);

  // Load supplier from URL query param on initial page load
  useEffect(() => {
    const rawSupplierNumbersByNormalized = new Map(
      (initialSupplierParam.current ?? '')
        .split(',')
        .map((supplierNumber) => supplierNumber.trim())
        .filter(Boolean)
        .map((supplierNumber) => [
          normalizeSupplierNumber(supplierNumber),
          supplierNumber
        ])
    );
    const supplierNumbers = parseSuppliersFromUrl(initialSupplierParam.current);
    if (!supplierNumbers.length || !info?.token || !hasAccess) {
      return;
    }
    let isCurrent = true; //stale closure guard, helps prevent state updates after re-running effect or unmounting
    Promise.all(
      supplierNumbers.map((supplierNumber) =>
        searchSuppliersByQuery(
          rawSupplierNumbersByNormalized.get(supplierNumber) ?? supplierNumber
        ).then((results) =>
          (results ?? []).find(
            (item) =>
              normalizeSupplierNumber(String(item.supplierNumber)) ===
              supplierNumber
          )
        )
      )
    )
      .then((matches) => {
        if (!isCurrent) {
          return;
        }
        const found = matches.filter(Boolean).map(mapSupplierResult);
        setSelectedSuppliers(found.slice(0, MAX_SUPPLIERS));
        const notFound = supplierNumbers.filter(
          (_supplierNumber, index) => !matches[index]
        );
        if (notFound.length) {
          setStatusErrorMsg(
            `Supplier number${notFound.length > 1 ? 's' : ''} ${notFound.join(', ')} ${notFound.length > 1 ? 'were' : 'was'} not found.`
          );
        }
      })
      .catch(() => {
        if (!isCurrent) return;
        setStatusErrorMsg("Couldn't load supplier from URL parameter.");
      });
    return () => {
      isCurrent = false;
    };
  }, [info?.token, hasAccess, setStatusErrorMsg]);

  // Fetch master scope data once the user is authenticated and authorized
  useEffect(() => {
    const masterScopeUrl = getEnvVar('VITE_MASTER_SCOPE_API_URL');
    if (!info?.token || !hasAccess || !masterScopeUrl) return;
    let isCurrent = true;
    fetchJsonWithAuth(masterScopeUrl)
      .then((response: any) => {
        if (!isCurrent) {
          return;
        }
        setMasterScope(transformMasterScopeResponse(response.json));
      })
      .catch((error: any) => {
        if (!isCurrent) {
          return;
        }
        setStatusErrorMsg(
          "Couldn't retrieve Unit List. Please try again.",
          error
        );
      });
    return () => {
      isCurrent = false;
    };
  }, [info?.token, hasAccess, setStatusErrorMsg]);

  useEffect(() => {
    let isCurrent = true;
    const activeSelection =
      selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
        ? selectedSuppliers
        : selectedSupplierGroup;

    async function fetchData() {
      setIsLoading(true);
      setSupplierSpendData(undefined);
      setSupplierBreakdown([]);
      let supplierNumbers: string[] = [];
      try {
        supplierNumbers =
          selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
            ? activeSelection.map((supplier: SupplierOption) => supplier.value)
            : activeSelection.supplierNumbers;
        const graphSupplierByNumber = new Map(
          graphSuppliers.map((supplier) => [supplier.value, supplier])
        );
        const request: SupplierSpendShiftRequestDTO = {
          supplierNumbers,
          ...(selectedUnits && selectedUnits.length > 0
            ? { unitCodes: selectedUnits }
            : {})
        };
        const queryParams = { pieces: spendShiftDisplayMode === 'pieces' };
        const supplierDetailRequests: Promise<SupplierDetailResult>[] =
          supplierNumbers.length > 1
            ? supplierNumbers.map(async (supplierNumber) => {
                const supplierRequest = {
                  ...request,
                  supplierNumbers: [supplierNumber]
                };
                const [detailResult, detailCiResult] = await Promise.allSettled(
                  [
                    getSupplierSpendShift(supplierRequest, queryParams),
                    getCiProjectSpendShift(supplierRequest, queryParams)
                  ]
                );
                const supplierSpend =
                  detailResult.status === 'fulfilled'
                    ? (detailResult.value as SupplierSpendShiftResponse)
                    : undefined;
                const ciSpend =
                  detailCiResult.status === 'fulfilled'
                    ? detailCiResult.value
                    : undefined;
                const hasPartialData =
                  !supplierSpend ||
                  !ciSpend ||
                  ciSpend.hadError === true ||
                  Object.values(supplierSpend.errorStatus ?? {}).some(Boolean);

                if (hasPartialData) {
                  return { supplierNumber, failed: true as const };
                }

                return {
                  supplierNumber,
                  failed: false as const,
                  spendData: enrichWithCiData(
                    supplierSpend,
                    ciSpend,
                    spendShiftDisplayMode
                  )
                };
              })
            : [];
        const mainResults = await Promise.allSettled([
          getSupplierSpendShift(request, queryParams),
          getCiProjectSpendShift(request, queryParams)
        ]);
        const [spendResult, ciResult] = mainResults;
        if (spendResult.status === 'rejected') {
          throw spendResult.reason;
        }

        const ciApiResponse: CiProjectSpendShiftApiResponse =
          ciResult.status === 'fulfilled'
            ? ciResult.value
            : { fiscalYears: [], projects: [], hadError: true };

        const supplierSpendShiftData =
          spendResult.value as SupplierSpendShiftResponse;
        if (!isCurrent) {
          return;
        }
        setSupplierSpendData(
          enrichWithCiData(
            supplierSpendShiftData,
            ciApiResponse,
            spendShiftDisplayMode
          )
        );
        const failingAPIs: string[] = [];
        if (supplierSpendShiftData.errorStatus?.invoiceSpendError) {
          failingAPIs.push('Invoice Spend');
        }
        if (supplierSpendShiftData.errorStatus?.forecastSpendError) {
          failingAPIs.push('Forecast Spend');
        }
        if (supplierSpendShiftData.errorStatus?.pdpProgramsError) {
          failingAPIs.push('PDP Programs');
        }
        if (ciApiResponse.hadError) {
          failingAPIs.push('CI Projects');
        }
        if (failingAPIs.length > 0) {
          const supplierContext =
            selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
            supplierNumbers.length > 1
              ? ` for suppliers ${supplierNumbers.join(', ')}`
              : '';
          setStatusWarningMsg(
            `Partial data retrieval failure${supplierContext}: ${failingAPIs.join(', ')}. Data may be incomplete or inaccurate.`
          );
        }
        setIsLoading(false);

        const supplierDetailResults = await Promise.allSettled(
          supplierDetailRequests
        );
        if (!isCurrent) {
          return;
        }
        const failedSupplierDetails = supplierDetailResults
          .map((result, index) =>
            result.status === 'fulfilled'
              ? result.value.failed
                ? result.value.supplierNumber
                : null
              : supplierNumbers[index]
          )
          .filter((supplierNumber): supplierNumber is string =>
            Boolean(supplierNumber)
          );
        const combinedDataComplete =
          ciResult.status === 'fulfilled' &&
          !ciApiResponse.hadError &&
          !Object.values(supplierSpendShiftData.errorStatus ?? {}).some(
            Boolean
          );
        if (failedSupplierDetails.length === 0 && combinedDataComplete) {
          setSupplierBreakdown(
            supplierDetailResults.flatMap((result) => {
              if (result.status !== 'fulfilled' || result.value.failed) {
                return [];
              }
              const { supplierNumber, spendData } = result.value;
              const supplier = graphSupplierByNumber.get(supplierNumber);
              return [
                {
                  supplierNumber,
                  supplierName: supplier?.supplierName || supplierNumber,
                  spendData
                }
              ];
            })
          );
        }
        if (failedSupplierDetails.length > 0) {
          failingAPIs.push(
            `Supplier breakdown for ${failedSupplierDetails.join(', ')}`
          );
        } else if (!combinedDataComplete && supplierNumbers.length > 1) {
          failingAPIs.push(
            'Supplier breakdown unavailable because combined data is incomplete'
          );
        }
        if (failingAPIs.length > 0) {
          const supplierContext =
            selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
            supplierNumbers.length > 1
              ? ` for suppliers ${supplierNumbers.join(', ')}`
              : '';
          setStatusWarningMsg(
            `Partial data retrieval failure${supplierContext}: ${failingAPIs.join(', ')}. Data may be incomplete or inaccurate.`
          );
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }
        setSupplierBreakdown([]);
        setStatusErrorMsg(
          supplierNumbers.length > 1
            ? `Couldn't fetch combined supplier spend shift data for ${supplierNumbers.join(', ')}. Please try again later.`
            : 'Error fetching supplier spend shift data. Please try again later.'
        );
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }

    if (
      activeSelection &&
      (selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
        ? activeSelection.length > 0
        : true)
    ) {
      if (
        selectionMode === SELECTION_MODE.SUPPLIER_GROUP &&
        !activeSelection.supplierNumbers
      ) {
        return () => {
          isCurrent = false;
        };
      }
      fetchData();
    } else {
      setSupplierSpendData(undefined);
      setSupplierBreakdown([]);
      setSortModel([]);
      setIsLoading(false);
    }
    return () => {
      isCurrent = false;
    };
  }, [
    selectedSuppliers,
    selectedSupplierGroup,
    selectionMode,
    selectedUnits,
    spendShiftDisplayMode,
    setStatusErrorMsg,
    setStatusWarningMsg,
    graphSuppliers
  ]);

  useEffect(() => {
    if (
      selectionMode !== SELECTION_MODE.SINGLE_SUPPLIER ||
      selectedSuppliers.length !== 1
    ) {
      setSroSbmInfo(null);
      return;
    }
    let isCurrent = true;
    // Set to undefined to indicate loading
    setSroSbmInfo(undefined);
    getSuppliersSroSbmMap(selectedSuppliers[0].value)
      .then((map: Map<string, any>) => {
        if (!isCurrent) return;
        setSroSbmInfo(map.get(String(selectedSuppliers[0].value)) ?? null);
      })
      .catch(() => {
        if (!isCurrent) return;
        setStatusErrorMsg(SRO_SBM_API_ERR_MSG);
        setSroSbmInfo(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [selectedSuppliers, selectionMode, setStatusErrorMsg]);

  const handleSuppliersChange = (suppliers: SupplierOption[]) => {
    setSelectedSuppliers(suppliers);
    clearAllMessages();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (suppliers.length > 0) {
          next.set(
            'supplierNumber',
            serializeSuppliersToUrl(suppliers.map((supplier) => supplier.value))
          );
        } else {
          next.delete('supplierNumber');
        }
        next.delete('supplierGroup');
        return next;
      },
      { replace: true }
    );
  };

  const handleSupplierGroupChange = useCallback(
    async (group: any) => {
      clearAllMessages();
      setSupplierSpendData(undefined);
      setSupplierBreakdown([]);
      setSortModel([]);
      setGroupMembers([]);
      if (!group) {
        setSelectedSupplierGroup(null);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('supplierGroup');
            return next;
          },
          { replace: true }
        );
        return;
      }
      setSelectedSupplierGroup(group);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('supplierGroup', String(group.value));
          next.delete('supplierNumber');
          return next;
        },
        { replace: true }
      );
      setIsLoading(true);
      try {
        const { data, error } = await supplierGroupApi.fetchClient.request(
          ...supplierGroupApi.operations['handleSearchRequest'],
          {
            body: { groupName: group.label }
          }
        );
        if (error) {
          throw error;
        }
        const filteredResults =
          data?.results?.filter((r) => r.jdsnGroup === group.label) ?? [];
        setGroupMembers(filteredResults);
        const supplierNumbers = filteredResults.map((r) => r.supplierNumber);
        setSelectedSupplierGroup({ ...group, supplierNumbers });
      } catch (error) {
        setStatusErrorMsg(
          "Couldn't fetch supplier numbers for the selected group."
        );
        setSelectedSupplierGroup(null);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('supplierGroup');
            return next;
          },
          { replace: true }
        );
        setIsLoading(false);
      }
    },
    [clearAllMessages, setSearchParams, setStatusErrorMsg]
  );

  useEffect(() => {
    if (
      selectionMode !== SELECTION_MODE.SUPPLIER_GROUP ||
      jdsnGroupOptions.length > 0 ||
      !info?.token ||
      !hasAccess
    )
      return;

    let isCurrent = true;
    async function fetchAllJdsnGroups() {
      setJdsnGroupsLoading(true);
      try {
        const response = await fetchJsonWithAuth(
          `${getEnvVar('VITE_JDSN_GROUPS_API_URL')}`
        );
        const data = response.json as JdsnGroupItem[];
        if (!isCurrent) return;
        setJdsnGroupOptions(
          data
            .map((item) => ({
              value: item.id,
              label: item.name
            }))
            .sort(sortByLabelAlphabetically)
        );
      } catch (error) {
        if (isCurrent) setStatusErrorMsg("Couldn't fetch JDSN Groups.");
      } finally {
        if (isCurrent) setJdsnGroupsLoading(false);
      }
    }

    fetchAllJdsnGroups();
    return () => {
      isCurrent = false;
    };
  }, [
    hasAccess,
    info?.token,
    jdsnGroupOptions.length,
    selectionMode,
    setStatusErrorMsg
  ]);

  const loadSupplierGroupOptions = (
    inputValue: string,
    callback: (options: any[]) => void
  ) => {
    const filtered =
      inputValue.length > 0
        ? jdsnGroupOptions.filter((opt) =>
            opt.label.toLowerCase().includes(inputValue.toLowerCase())
          )
        : jdsnGroupOptions;
    callback(filtered);
  };

  useEffect(() => {
    const groupId = initialGroupParam.current;
    if (!groupId || !info?.token || !hasAccess || !jdsnGroupOptions.length) {
      return;
    }
    const group = jdsnGroupOptions.find(
      (option) => String(option.value) === String(groupId)
    );
    if (group) {
      initialGroupParam.current = null;
      setSelectionMode(SELECTION_MODE.SUPPLIER_GROUP);
      handleSupplierGroupChange(group);
    } else if (!jdsnGroupsLoading) {
      initialGroupParam.current = null;
      setStatusErrorMsg(`Supplier Group "${groupId}" was not found.`);
    }
  }, [
    hasAccess,
    info?.token,
    jdsnGroupOptions,
    jdsnGroupsLoading,
    setStatusErrorMsg,
    handleSupplierGroupChange
  ]);

  const sortedGroupMembers = useMemo(() => {
    return [...groupMembers].sort((a, b) =>
      a.supplierNumber.localeCompare(b.supplierNumber, undefined, {
        numeric: true
      })
    );
  }, [groupMembers]);

  const columns: GridColDef<SpendShiftRow>[] = useMemo(() => {
    const fmt =
      spendShiftDisplayMode === 'pieces'
        ? formatPiecesAndPercentage
        : formatSpendAndPercentage;
    return [
      {
        field: 'name',
        renderCell: ({ row }: { row: SpendShiftRow }) => {
          switch (row.type) {
            case 'currentSpend':
              return (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant={'labelMedium'}
                    sx={(theme) => ({
                      mr: theme.fuel6Tokens.JddcSpacingXSmall
                    })}
                  >
                    {spendShiftDisplayMode === 'pieces'
                      ? 'Current Pieces'
                      : 'Current Spend'}
                  </Typography>
                  <Tooltip
                    title={`R12 Forecast spend is assumed to carry forward for the next three fiscal years.`}
                    placement={'top-start'}
                  >
                    <fuel-icon name="info_outlined" size="medium" />
                  </Tooltip>
                </Box>
              );

            case 'pdpHeader':
              return (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant={'labelMedium'}
                    sx={(theme) => ({
                      mr: theme.fuel6Tokens.JddcSpacingXSmall
                    })}
                  >
                    PDP Program (from SEP)
                  </Typography>
                  <Tooltip
                    title={`Program spend is calculated from Current Rep Machine and PDP Rep Machine SAP MBOMs and volumes entered on SEP Program pages by SCI Leads. Prices and suppliers come from the unit's scheduling agreements or MIS records for new parts.  On this page, we filter the rep parts for the selected supplier and Current Rep Machine parts are subtracted out and PDP Rep Machine parts are added in starting in the program's production year and beyond. This includes PDP spend changes for SAP units on PAG, PA2 and PAA only.  Data is refreshed overnight.`}
                    placement={'top-start'}
                  >
                    <fuel-icon name="info_outlined" size="medium" />
                  </Tooltip>
                </Box>
              );
            case 'totalSpend':
              return (
                <Typography fontWeight={'bold'}>
                  {spendShiftDisplayMode === 'pieces'
                    ? 'Total Pieces'
                    : 'Total Spend'}
                </Typography>
              );
            case 'pdpProgram':
              return (
                <Tooltip title={`#${row.csProgramId} ${row.name}`}>
                  <Link
                    href={
                      getEnvVar('VITE_SEP_APP_BASE_URL') +
                      `/viewProgram/${row.csProgramId}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="linkExternal"
                  >
                    #{row.csProgramId} {row.name}
                  </Link>
                </Tooltip>
              );
            case 'ciHeader':
              return (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant={'labelMedium'}
                    sx={(theme) => ({
                      mr: theme.fuel6Tokens.JddcSpacingXSmall
                    })}
                  >
                    {CI_PROJECTS_LABEL}
                  </Typography>
                  <Tooltip title={CI_PROJECTS_TOOLTIP} placement={'top-start'}>
                    <fuel-icon name="info_outlined" size="medium" />
                  </Tooltip>
                </Box>
              );
            case 'ciProject':
              return (
                <Tooltip title={`#${row.projectId} ${row.projectTitle}`}>
                  <Link
                    href={
                      getEnvVar('VITE_CI_PROJECT_EDIT_LINK') + row.projectId
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="linkExternal"
                  >
                    #{row.projectId} {row.projectTitle}
                  </Link>
                </Tooltip>
              );
          }
        },
        headerName: ' ',
        valueGetter: (value: unknown, row?: SpendShiftRow) => {
          const gridRow = getSpendShiftGridRow(value, row);

          if (!gridRow) return '';

          switch (gridRow.type) {
            case 'currentSpend':
              return spendShiftDisplayMode === 'pieces'
                ? 'Current Pieces'
                : 'Current Spend';
            case 'pdpHeader':
              return 'PDP Program';
            case 'totalSpend':
              return spendShiftDisplayMode === 'pieces'
                ? 'Total Pieces'
                : 'Total Spend';
            case 'pdpProgram':
              return `#${gridRow.csProgramId} ${gridRow.name}`;
            case 'ciHeader':
              return CI_PROJECTS_LABEL;
            case 'ciProject':
              return `#${gridRow.projectId} ${gridRow.projectTitle}`;
            default:
              return '';
          }
        },
        sortComparator: (v1: any, v2: any, p1: any, p2: any) => {
          const r1 = p1.api?.getRow(p1.id);
          const r2 = p2.api?.getRow(p2.id);
          const gDiff = getGroupOrder(r1) - getGroupOrder(r2);
          if (gDiff !== 0) return gDiff;
          return String(v1 ?? '').localeCompare(String(v2 ?? ''));
        },
        minWidth: 260,
        flex: 2,
        cellClassName: 'name-cell'
      },
      {
        field: 'status',
        headerName: 'Status',
        renderHeader: () => <Typography fontWeight={'bold'}>Status</Typography>,
        width: 130,
        sortComparator: (v1: any, v2: any, p1: any, p2: any) => {
          const r1 = p1.api?.getRow(p1.id);
          const r2 = p2.api?.getRow(p2.id);
          const gDiff = getGroupOrder(r1) - getGroupOrder(r2);
          if (gDiff !== 0) return gDiff;
          return String(v1 ?? '').localeCompare(String(v2 ?? ''));
        },
        valueGetter: (value: unknown, row?: SpendShiftRow) => {
          const gridRow = getSpendShiftGridRow(value, row);
          if (!gridRow) return '';
          if (gridRow.type === 'pdpProgram')
            return gridRow.csProgramStatus ?? '—';
          if (gridRow.type === 'ciProject') return gridRow.projectStatus ?? '—';
          return '';
        },
        renderCell: ({ row }: { row: SpendShiftRow }) => {
          if (row.type === 'pdpProgram')
            return <Typography>{row.csProgramStatus ?? '—'}</Typography>;
          if (row.type === 'ciProject')
            return <Typography>{row.projectStatus ?? '—'}</Typography>;
          return null;
        }
      },
      {
        field: 'sop',
        headerName: 'SOP',
        minWidth: 115,
        renderHeader: () => (
          <Typography
            fontWeight={'bold'}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            SOP
            <Tooltip
              title={
                <>
                  <div>Start of Production for PDP Programs</div>
                  <div>Implementation Date for CI Projects</div>
                </>
              }
            >
              <fuel-icon name="info_outlined" />
            </Tooltip>
          </Typography>
        ),
        sortComparator: (_v1: any, _v2: any, p1: any, p2: any) => {
          const r1 = p1.api?.getRow(p1.id);
          const r2 = p2.api?.getRow(p2.id);
          const gDiff = getGroupOrder(r1) - getGroupOrder(r2);
          if (gDiff !== 0) return gDiff;
          const d1 =
            r1?.type === 'pdpProgram' ? r1.sop : (r1?.implDate ?? null);
          const d2 =
            r2?.type === 'pdpProgram' ? r2.sop : (r2?.implDate ?? null);
          if (!d1 && !d2) return 0;
          if (!d1) return 1;
          if (!d2) return -1;
          return new Date(d1).getTime() - new Date(d2).getTime();
        },
        valueGetter: (value: unknown, row?: SpendShiftRow) => {
          const gridRow = getSpendShiftGridRow(value, row);

          if (gridRow?.type === 'pdpProgram') return gridRow.sop ?? null;
          if (gridRow?.type === 'ciProject') return gridRow.implDate ?? null;
          return null;
        },
        renderCell: ({ row }: { row: SpendShiftRow }) => {
          if (row.type === 'pdpProgram')
            return <Typography>{row.sop ?? '—'}</Typography>;
          if (row.type === 'ciHeader') return null;
          if (row.type === 'ciProject')
            return <Typography>{row.implDate ?? '—'}</Typography>;
          return null;
        }
      },
      {
        field: 'unitCode',
        renderHeader: () => (
          <Typography fontWeight={'bold'}>Unit Code</Typography>
        ),
        headerName: 'Unit Code',
        width: 120,
        sortComparator: (v1: any, v2: any, p1: any, p2: any) => {
          const r1 = p1.api?.getRow(p1.id);
          const r2 = p2.api?.getRow(p2.id);
          const gDiff = getGroupOrder(r1) - getGroupOrder(r2);
          if (gDiff !== 0) return gDiff;
          return String(v1 ?? '').localeCompare(String(v2 ?? ''));
        },
        renderCell: ({ row }: { row: SpendShiftRow }) => {
          switch (row.type) {
            case 'pdpProgram':
              return <Typography>{row.unitCode}</Typography>;
            case 'ciProject':
              return <Typography>{row.unitCode}</Typography>;
          }
        }
      },
      {
        field: 'r12Invoiced',
        headerName: 'R12 Invoiced',
        renderHeader: () => (
          <Typography fontWeight={'bold'}>R12 Invoiced</Typography>
        ),
        minWidth: 175,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        sortComparator: (_v1: any, _v2: any, p1: any, p2: any) => {
          const r1 = p1.api?.getRow(p1.id);
          const r2 = p2.api?.getRow(p2.id);
          const gDiff = getGroupOrder(r1) - getGroupOrder(r2);
          if (gDiff !== 0) return gDiff;
          const a1 =
            r1 && 'r12InvoiceSpend' in r1 ? r1.r12InvoiceSpend.amount : 0;
          const a2 =
            r2 && 'r12InvoiceSpend' in r2 ? r2.r12InvoiceSpend.amount : 0;
          return a1 - a2;
        },
        valueGetter: (value: unknown, row?: SpendShiftRow) => {
          const gridRow = getSpendShiftGridRow(value, row);

          if (!gridRow) return '';

          switch (gridRow.type) {
            case 'currentSpend':
            case 'pdpHeader':
            case 'pdpProgram':
              return fmt(gridRow.r12InvoiceSpend);
            case 'totalSpend':
              return fmt(gridRow.r12InvoiceSpend, false);
            case 'ciHeader':
            case 'ciProject':
              return fmt(gridRow.r12InvoiceSpend);
            default:
              return '';
          }
        },
        renderCell: ({ row }: { row: SpendShiftRow }) => {
          switch (row.type) {
            case 'currentSpend':
            case 'pdpHeader':
              return (
                <Typography fontWeight={'bold'}>
                  {fmt(row.r12InvoiceSpend)}
                </Typography>
              );
            case 'pdpProgram':
              return <Typography>{fmt(row.r12InvoiceSpend)}</Typography>;
            case 'totalSpend':
              return (
                <Typography fontWeight={'bold'}>
                  {fmt(row.r12InvoiceSpend, false)}
                </Typography>
              );
            case 'ciHeader':
              return (
                <Typography fontWeight={'bold'}>
                  {fmt(row.r12InvoiceSpend)}
                </Typography>
              );
            case 'ciProject':
              return <Typography>{fmt(row.r12InvoiceSpend)}</Typography>;
          }
        }
      },
      {
        field: 'r12Forecasted',
        headerName: 'R12 Forecasted',
        renderHeader: () => (
          <Typography fontWeight={'bold'}>R12 Forecasted</Typography>
        ),
        minWidth: 175,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        sortComparator: (_v1: any, _v2: any, p1: any, p2: any) => {
          const r1 = p1.api?.getRow(p1.id);
          const r2 = p2.api?.getRow(p2.id);
          const gDiff = getGroupOrder(r1) - getGroupOrder(r2);
          if (gDiff !== 0) return gDiff;
          const a1 =
            r1 && 'r12ForecastSpend' in r1 ? r1.r12ForecastSpend.amount : 0;
          const a2 =
            r2 && 'r12ForecastSpend' in r2 ? r2.r12ForecastSpend.amount : 0;
          return a1 - a2;
        },
        valueGetter: (value: unknown, row?: SpendShiftRow) => {
          const gridRow = getSpendShiftGridRow(value, row);

          if (!gridRow) return '';

          switch (gridRow.type) {
            case 'currentSpend':
            case 'pdpHeader':
            case 'pdpProgram':
              return fmt(gridRow.r12ForecastSpend);
            case 'totalSpend':
              return fmt(gridRow.r12ForecastSpend, false);
            case 'ciHeader':
            case 'ciProject':
              return fmt(gridRow.r12ForecastSpend);
            default:
              return '';
          }
        },
        renderCell: ({ row }: { row: SpendShiftRow }) => {
          switch (row.type) {
            case 'currentSpend':
            case 'pdpHeader':
              return (
                <Typography fontWeight={'bold'}>
                  {fmt(row.r12ForecastSpend)}
                </Typography>
              );
            case 'pdpProgram':
              return <Typography>{fmt(row.r12ForecastSpend)}</Typography>;
            case 'ciHeader':
              return (
                <Typography fontWeight={'bold'}>
                  {fmt(row.r12ForecastSpend)}
                </Typography>
              );
            case 'ciProject':
              return <Typography>{fmt(row.r12ForecastSpend)}</Typography>;
            case 'totalSpend':
              return (
                <Typography fontWeight={'bold'}>
                  {fmt(row.r12ForecastSpend, false)}
                </Typography>
              );
          }
        }
      },
      ...(supplierSpendData
        ? supplierSpendData.fiscalYears.map(
            (item, index): GridColDef<SpendShiftRow> => ({
              field: item,
              headerName: `FY${item}`,
              renderHeader: () => (
                <Typography fontWeight={'bold'}>FY{item}</Typography>
              ),
              minWidth: 165,
              flex: 0.8,
              align: 'right',
              headerAlign: 'right',
              sortComparator: (
                _v1: any,
                _v2: any,
                cellParams1: any,
                cellParams2: any
              ) => {
                const row1 = cellParams1.api?.getRow(cellParams1.id);
                const row2 = cellParams2.api?.getRow(cellParams2.id);
                const gDiff = getGroupOrder(row1) - getGroupOrder(row2);
                if (gDiff !== 0) return gDiff;
                const amount1 =
                  row1 && 'fiscalYears' in row1
                    ? (row1.fiscalYears[index]?.amount ?? 0)
                    : 0;
                const amount2 =
                  row2 && 'fiscalYears' in row2
                    ? (row2.fiscalYears[index]?.amount ?? 0)
                    : 0;
                return amount1 - amount2;
              },
              valueGetter: (value: unknown, row?: SpendShiftRow) => {
                const gridRow = getSpendShiftGridRow(value, row);

                if (gridRow && 'fiscalYears' in gridRow) {
                  return fmt(
                    gridRow.fiscalYears?.[index],
                    gridRow.type !== 'totalSpend'
                  );
                }
                return '';
              },
              renderCell: ({ row }: { row: SpendShiftRow }) => {
                switch (row.type) {
                  case 'currentSpend':
                  case 'pdpHeader':
                    return (
                      <Typography fontWeight={'bold'}>
                        {fmt(row.fiscalYears?.[index])}
                      </Typography>
                    );
                  case 'pdpProgram':
                    return (
                      <Typography>{fmt(row.fiscalYears?.[index])}</Typography>
                    );
                  case 'totalSpend':
                    return (
                      <Typography fontWeight={'bold'}>
                        {fmt(row.fiscalYears?.[index], false)}
                      </Typography>
                    );
                  case 'ciHeader':
                    return (
                      <Typography fontWeight={'bold'}>
                        {fmt(row.fiscalYears?.[index])}
                      </Typography>
                    );
                  case 'ciProject':
                    return (
                      <Typography>{fmt(row.fiscalYears?.[index])}</Typography>
                    );
                  default:
                    return null;
                }
              }
            })
          )
        : [])
    ];
  }, [supplierSpendData, spendShiftDisplayMode]);

  return !info ? (
    <></>
  ) : !hasAccess ? (
    <NoAccessEmployee userId={loggedInUser} />
  ) : (
    <>
      <PageHeader>
        <PageHeaderTitle>
          <Typography variant="h1">Supplier Spend Shift</Typography>
        </PageHeaderTitle>
        <PageHeaderActions>
          <ToggleButtonGroup
            exclusive
            value={spendShiftDisplayMode}
            onChange={(_event, newValue) => {
              if (!newValue) return;
              setSpendShiftDisplayMode(newValue);
            }}
            aria-label="Spend shift display mode"
            variant="segmented"
          >
            <ToggleButton value="spend">Spend</ToggleButton>
            <ToggleButton value="pieces">Pieces</ToggleButton>
          </ToggleButtonGroup>
        </PageHeaderActions>
      </PageHeader>

      <PageContent
        sx={(theme) => ({
          minWidth: 0,
          marginTop: `calc(-1 * ${theme.fuel6Tokens.JddcSpacingLarge} + ${theme.fuel6Tokens.JddcSpacingSmall})`
        })}
      >
        <MessageBanner
          statusMessages={statusMessages}
          apiInProgressCount={apiInProgressCount}
        />

        <Grid container>
          <Grid size={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  <ToggleButtonGroup
                    exclusive
                    value={selectionMode}
                    onChange={(event, newValue) => {
                      if (!newValue) return;
                      setSelectionMode(newValue);
                      if (newValue === SELECTION_MODE.SINGLE_SUPPLIER) {
                        setSearchParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            next.delete('supplierGroup');
                            if (selectedSuppliers.length) {
                              next.set(
                                'supplierNumber',
                                serializeSuppliersToUrl(
                                  selectedSuppliers.map(
                                    (supplier) => supplier.value
                                  )
                                )
                              );
                            } else {
                              next.delete('supplierNumber');
                            }
                            return next;
                          },
                          { replace: true }
                        );
                      } else if (newValue === SELECTION_MODE.SUPPLIER_GROUP) {
                        setSearchParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            next.delete('supplierNumber');
                            if (selectedSupplierGroup) {
                              next.set(
                                'supplierGroup',
                                String(selectedSupplierGroup.value)
                              );
                            }
                            return next;
                          },
                          { replace: true }
                        );
                      }
                    }}
                    aria-label="Supplier selection mode"
                    variant="segmented"
                    sx={{ flexShrink: 0 }}
                  >
                    <ToggleButton value={SELECTION_MODE.SINGLE_SUPPLIER}>
                      Suppliers
                    </ToggleButton>
                    <ToggleButton value={SELECTION_MODE.SUPPLIER_GROUP}>
                      Supplier Group
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Box
                    sx={{
                      flex: '1 1 360px',
                      minWidth: 'min(100%, 320px)'
                    }}
                  >
                    {selectionMode === SELECTION_MODE.SINGLE_SUPPLIER ? (
                      <SupplierMultiSelect
                        suppliers={selectedSuppliers}
                        onChange={handleSuppliersChange}
                        setStatusErrorMsg={setStatusErrorMsg}
                      />
                    ) : (
                      <SupplierDropDownSearch
                        key={selectionMode}
                        showFilter={true}
                        setStatusErrorMsg={setStatusErrorMsg}
                        handleInputChange={handleSupplierGroupChange}
                        name="supplierGroup"
                        suppliers={selectedSupplierGroup}
                        showSingleOption={true}
                        loadOptionsOverride={loadSupplierGroupOptions}
                        defaultOptions={jdsnGroupOptions}
                        placeholderText={
                          jdsnGroupsLoading
                            ? 'Loading JDSN Groups...'
                            : 'Search by Group Name'
                        }
                      />
                    )}
                  </Box>
                  <ScopeQuickFilter
                    masterInputTreeJson={masterScope}
                    selectedUnits={selectedUnits}
                    onApply={(units: string[]) =>
                      setSelectedUnits(units && units.length > 0 ? units : null)
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
          selectedSuppliers.length === 0 && (
            <Card sx={{ mt: 2, p: 3 }}>
              <Typography>
                Select or paste supplier numbers to view combined spend.
              </Typography>
            </Card>
          )}
        {((selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
          selectedSuppliers.length > 0) ||
          (selectionMode === SELECTION_MODE.SUPPLIER_GROUP &&
            selectedSupplierGroup)) && (
          <Card sx={{ marginTop: '1rem', padding: '1rem' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                width: '100%',
                gap: 1,
                mb: 2
              }}
            >
              <Box sx={{ mb: '1rem' }}>
                <>
                  {selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
                    selectedSuppliers.length === 1 && (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            display: 'block'
                          }}
                        >
                          SRO Supply Base Manager
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.875rem', display: 'block' }}
                        >
                          {sroSbmInfo === undefined
                            ? 'Loading…'
                            : sroSbmInfo && sroSbmInfo.userId
                              ? getFormattedUserNameWithId(
                                  sroSbmInfo,
                                  sroSbmInfo.userId
                                )
                              : '—'}
                        </Typography>
                      </>
                    )}
                </>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  ml: 'auto',
                  gap: 4
                }}
              >
                {selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
                  selectedSuppliers.length === 1 && (
                    <Button
                      component="a"
                      href={`https://controltower.deere.com/#/actionboard/Supplier_Insights?onePager=${selectedSuppliers[0].value}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant={'tertiary'}
                      endIcon={<fuel-icon name={'open_in_new'} />}
                    >
                      CT Supplier Snapshot
                    </Button>
                  )}
                {selectionMode === SELECTION_MODE.SINGLE_SUPPLIER &&
                  selectedSuppliers.length === 1 && (
                    <Button
                      component="a"
                      href={`https://controltower.deere.com/#/actionboard/Supplier_Insights/tcm?supplier=${selectedSuppliers[0].value}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant={'tertiary'}
                      endIcon={<fuel-icon name={'open_in_new'} />}
                    >
                      CT Contract Insights
                    </Button>
                  )}
                {selectionMode === SELECTION_MODE.SUPPLIER_GROUP && (
                  <Button
                    variant="tertiary"
                    onClick={() => setShowGroupMembersModal(true)}
                    endIcon={<fuel-icon name={'visibility'} />}
                    sx={{ textDecoration: 'underline' }}
                  >
                    View All Suppliers in Group
                  </Button>
                )}
                {(info.isCommMgr || info.isSBM || info.isAdmin) && (
                  <Button
                    variant={'tertiary'}
                    endIcon={<fuel-icon name="download" />}
                    onClick={() => {
                      const activeSelection =
                        selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
                          ? {
                              value: selectedSuppliers[0]?.value,
                              label:
                                selectedSuppliers.length === 1
                                  ? selectedSuppliers[0].label
                                  : selectedSuppliers
                                      .map(
                                        (supplier) => supplier.supplierNumber
                                      )
                                      .join(', ')
                            }
                          : selectedSupplierGroup;
                      const supplierNumbers =
                        selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
                          ? selectedSuppliers.map((supplier) => supplier.value)
                          : activeSelection.supplierNumbers;
                      exportSupplierSpendShiftPartDetails(
                        {
                          supplierNumbers,
                          ...(selectedUnits && selectedUnits.length > 0
                            ? { unitCodes: selectedUnits }
                            : {})
                        },
                        activeSelection.label,
                        supplierSpendData!,
                        spendShiftDisplayMode
                      );
                    }}
                  >
                    Part Detail
                  </Button>
                )}
              </Box>
            </Box>
            {isLoading && (
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  mt: '1rem',
                  mb: '1rem'
                }}
              >
                <CircularProgress
                  data-fuel-size="small"
                  sx={{
                    width: '1.5rem',
                    height: '1.5rem',
                    marginRight: '.5rem'
                  }}
                />
                <Typography variant={'paragraph'}>
                  Fetching supplier shift data
                </Typography>
              </Grid>
            )}
            {supplierSpendData && (
              <>
                <SupplierSpendShiftSummary
                  supplierSpendData={supplierSpendData}
                  spendShiftDisplayMode={spendShiftDisplayMode}
                  errorStatus={supplierSpendData.errorStatus}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '1rem'
                  }}
                >
                  <Box
                    data-testid="spend-shift-graph"
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      height: 'fit-content',
                      gap: '10px',
                      padding: theme.fuel6Tokens.JddcSpacingMedium,
                      border: `1px solid ${theme.fuel6Tokens.JddcColorBorderSubtle}`,
                      borderRadius: theme.fuel6Tokens.JddcRadiusMedium,
                      backgroundColor:
                        theme.fuel6Tokens.JddcColorBackgroundPrimary
                    })}
                  >
                    <SupplierSpendShiftGraph
                      spendData={supplierSpendData}
                      spendShiftDisplayMode={spendShiftDisplayMode}
                      selectionMode={selectionMode}
                      errorStatus={supplierSpendData.errorStatus}
                      supplierBreakdown={supplierBreakdown}
                      supplierCount={graphSuppliers.length}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Card>
        )}

        {supplierSpendData && (
          <Card sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingMedium })}>
            <CardContent>
              <DataGridPro
                data-testid="spend-shift-grid"
                columns={columns}
                autoHeight
                density="compact"
                disableVirtualization={import.meta.env.MODE === 'test'}
                sx={(theme) => supplierSpendShiftGridStyle(theme)}
                showToolbar
                slots={{ toolbar: CustomToolbar }}
                slotProps={{
                  toolbar: {
                    supplierNumber:
                      selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
                        ? selectedSuppliers
                            .map((supplier) => supplier.value)
                            .join(',')
                        : selectedSupplierGroup?.value,
                    supplierName:
                      selectionMode === SELECTION_MODE.SINGLE_SUPPLIER
                        ? selectedSuppliers
                            .map((supplier) => supplier.supplierName)
                            .join(', ')
                        : selectedSupplierGroup?.label,
                    spendShiftDisplayMode: spendShiftDisplayMode
                  }
                }}
                sortModel={sortModel}
                onSortModelChange={(newModel) => setSortModel(newModel)}
                pinnedRows={{
                  top: [
                    {
                      id: 'currentSpend',
                      type: 'currentSpend' as const,
                      r12InvoiceSpend:
                        supplierSpendData.currentSpends.r12InvoiceSpend,
                      r12ForecastSpend:
                        supplierSpendData.currentSpends.r12ForecastSpend,
                      fiscalYears: supplierSpendData.currentSpends.fiscalYears
                    },
                    {
                      id: 'pdpHeader',
                      type: 'pdpHeader' as const,
                      r12InvoiceSpend:
                        supplierSpendData.totalPDPSpends.r12InvoiceSpend,
                      r12ForecastSpend:
                        supplierSpendData.totalPDPSpends.r12ForecastSpend,
                      fiscalYears: supplierSpendData.totalPDPSpends.fiscalYears
                    }
                  ],
                  bottom: [
                    {
                      id: 'totalSpend',
                      type: 'totalSpend' as const,
                      r12InvoiceSpend:
                        supplierSpendData.totalSpends.r12InvoiceSpend,
                      r12ForecastSpend:
                        supplierSpendData.totalSpends.r12ForecastSpend,
                      fiscalYears: supplierSpendData.totalSpends.fiscalYears
                    }
                  ]
                }}
                rows={[
                  ...supplierSpendData.pdpPrograms.map((pdpProgram) => ({
                    id: `pdpProgram-${pdpProgram.csProgramId}`,
                    type: 'pdpProgram' as const,
                    name: pdpProgram.csProgramName,
                    sop: pdpProgram.sop,
                    unitCode: pdpProgram.unitCode,
                    r12InvoiceSpend: pdpProgram.r12InvoiceSpend,
                    r12ForecastSpend: pdpProgram.r12ForecastSpend,
                    fiscalYears: pdpProgram.fiscalYears,
                    csProgramId: pdpProgram.csProgramId,
                    csProgramStatus: pdpProgram.csProgramStatus ?? null
                  })),
                  {
                    id: 'ciHeader',
                    type: 'ciHeader' as const,
                    r12InvoiceSpend:
                      supplierSpendData.totalCISpends!.r12InvoiceSpend,
                    r12ForecastSpend:
                      supplierSpendData.totalCISpends!.r12ForecastSpend,
                    fiscalYears: supplierSpendData.totalCISpends!.fiscalYears
                  },
                  ...(supplierSpendData.ciProjects ?? []).map((ci) => ({
                    id: `ciProject-${ci.projectId}`,
                    type: 'ciProject' as const,
                    projectId: ci.projectId,
                    projectTitle: ci.projectTitle,
                    projectStatus: ci.projectStatus ?? null,
                    unitCode: ci.unitCode,
                    implDate: ci.implDate,
                    r12InvoiceSpend: ci.r12InvoiceSpend,
                    r12ForecastSpend: ci.r12ForecastSpend,
                    fiscalYears: ci.fiscalYears
                  }))
                ]}
              />
            </CardContent>
          </Card>
        )}
        <Dialog
          open={showGroupMembersModal}
          onClose={() => setShowGroupMembersModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant={'h3'}>
              Supplier Group: {selectedSupplierGroup?.label}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowGroupMembersModal(false)}
              aria-label="close"
            >
              <fuel-icon name="close" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: 'white', fontWeight: 600 }}>
                    Supplier Number
                  </TableCell>
                  <TableCell sx={{ backgroundColor: 'white', fontWeight: 600 }}>
                    Supplier Name
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGroupMembers.map((member) => (
                  <TableRow key={member.supplierNumber}>
                    <TableCell>{member.supplierNumber}</TableCell>
                    <TableCell>
                      {member.supplier?.supplierName ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
      </PageContent>
    </>
  );
}
